import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    MdArrowBack,
    MdSearch,
    MdLocationOn,
    MdExpandMore,
    MdExpandLess,
    MdStore,
    MdAssignment,
    MdDescription,
    MdCurrencyRupee,
    MdFilterList,
    MdFileDownload
} from 'react-icons/md';
import styles from './VendorProjectSummary.module.css';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import Drawer from '../components/common/Drawer';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { authFetch } from '../components/utils/authFetch';
import { GET_VENDOR_PROJECT_SUMMARY_API, GET_ALL_PROJECT_API } from '../config/API';

const VendorProjectSummary = () => {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [vendorDetails, setVendorDetails] = useState(null);
    const [summary, setSummary] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showStats, setShowStats] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setShowStats(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchProjectSummary = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=${limit}&search=${debouncedSearch}`;
            
            if (selectedProject?.value) {
                queryParams += `&projectId=${selectedProject.value}`;
            }

            const response = await authFetch(
                `${GET_VENDOR_PROJECT_SUMMARY_API(vendorId)}${queryParams}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            const result = await response.json();

            if (result.status) {
                setData(result.data || []);
                setVendorDetails(result.vendorDetails);
                setSummary(result.summary);
                const pagination = result.pagination;
                setTotalPages(pagination?.totalPages || 1);
                setTotalRecords(pagination?.totalCount || result.data?.length || 0);
            } else {
                setData([]);
                setVendorDetails(null);
                setSummary(null);
                setTotalPages(1);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching vendor project summary:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;
            
            if (selectedProject?.value) {
                queryParams += `&projectId=${selectedProject.value}`;
            }

            const response = await authFetch(
                `${GET_VENDOR_PROJECT_SUMMARY_API(vendorId)}${queryParams}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('An error occurred during export');
        }
    };

    useEffect(() => {
        fetchProjectSummary();
    }, [vendorId, currentPage, debouncedSearch, selectedProject, limit]);

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return `₹${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <div className={styles['purchase-orders-container']}>
            {/* Page Header */}
            <div className={styles['page-header']}>
                <button 
                    onClick={() => window.history.length > 1 ? navigate(-1) : window.close()} 
                    className={styles['back-btn']}
                >
                    <MdArrowBack size={20} />
                </button>
                <div className={styles['title-section']}>
                    <h1>{vendorDetails?.vendorName || 'Vendor Summary'}</h1>
                    <div className={styles['page-subtitle']}>
                        <MdStore /> {vendorDetails?.companyName}
                        {vendorDetails?.category && (
                            <span className={styles['vendor-category']}>{vendorDetails.category}</span>
                        )}
                    </div>
                </div>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                    <button
                        className={styles['export-btn']}
                        onClick={handleExportCSV}
                    >
                        <MdFileDownload size={18} /> Export CSV
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Project</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_PROJECT_API}
                            mapResponse={(p) => ({
                                value: p.id || p._id,
                                label: p.name || p.projectName
                            })}
                            value={selectedProject}
                            onChange={(option) => setSelectedProject(option)}
                            placeholder="Select Project"
                            isClearable
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setSelectedProject(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset Filter
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* Stats Toggle (Mobile Only) */}
            <button 
                className={styles['stats-toggle-btn']}
                onClick={() => setShowStats(!showStats)}
            >
                {showStats ? <MdExpandLess /> : <MdExpandMore />}
                {showStats ? 'Hide Summary' : 'Show Summary'}
            </button>

            {/* Stats Grid */}
            <div className={`${styles['stats-grid']} ${showStats ? styles.show : styles.hide}`}>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdAssignment />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Total Projects</span>
                    <span className={styles['stat-value']}>{summary?.totalProjects || 0}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['purple']}`}>
                            <MdDescription />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Total POs</span>
                    <span className={styles['stat-value']}>{summary?.totalPOs || 0}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Committed Amount</span>
                    <span className={`${styles['stat-value']} ${styles['amount-committed']}`}>
                        {formatCurrency(summary?.totalAmount)}
                    </span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['green']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Paid Amount</span>
                    <span className={`${styles['stat-value']} ${styles['amount-paid']}`}>
                        {formatCurrency(summary?.totalPaidAmount)}
                    </span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['red']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Remaining</span>
                    <span className={`${styles['stat-value']} ${styles['amount-remaining']}`}>
                        {formatCurrency(summary?.totalRemainingAmount)}
                    </span>
                </div>
            </div>

            {/* Table Card */}
            <div className={styles['card-container-po']}>
                <div className={styles['search-container']}>
                    <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {!isMobile ? (
                    <div className={styles['table-container']}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Project Name</th>
                                    <th>Location</th>
                                    <th className={styles['text-center']}>Total POs</th>
                                    <th className={styles['text-right']}>Total Amount</th>
                                    <th className={styles['text-right']}>Paid Amount</th>
                                    <th className={styles['text-right']}>Remaining</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <Loader text="Fetching project list..." />
                                        </td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <NoData title="No Projects Found" message="No project data available for this vendor." />
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((project) => (
                                        <tr key={project.projectId}>
                                            <td className={styles['project-name-cell']}>{project.projectName}</td>
                                            <td>
                                                <div className={styles['address-cell']}>
                                                    <MdLocationOn /> {project.projectAdd}
                                                </div>
                                            </td>
                                            <td className={styles['text-center']}>
                                                <span 
                                                    className={styles['link-text']}
                                                    onClick={() => window.open(`/reports/project/${project.projectId}/vendor/${vendorId}/pos`, '_blank')}
                                                >
                                                    {project.totalPOs}
                                                </span>
                                            </td>
                                            <td className={`${styles['text-right']} ${styles['amount-committed']}`}>{formatCurrency(project.totalAmount)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-paid']}`}>{formatCurrency(project.totalPaidAmount)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-remaining']}`}>{formatCurrency(project.totalRemainingAmount)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles['mobile-card-view']}>
                        {loading ? (
                            <div style={{ padding: '2rem' }}>
                                <Loader text="Fetching projects..." />
                            </div>
                        ) : data.length === 0 ? (
                            <NoData title="No Projects Found" message="Try adjusting your search." />
                        ) : (
                            data.map((project) => (
                                <div key={project.projectId} className={styles['project-card']}>
                                    <div className={styles['card-header']}>
                                        {project.projectName}
                                    </div>
                                    <div className={styles['card-body']}>
                                        <div className={styles['address-row']}>
                                            <MdLocationOn /> {project.projectAdd}
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total POs:</span>
                                            <span 
                                                className={`${styles['card-value']} ${styles['link-text']}`}
                                                onClick={() => window.open(`/reports/project/${project.projectId}/vendor/${vendorId}/pos`, '_blank')}
                                            >
                                                {project.totalPOs}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total Amount:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-committed']}`}>
                                                {formatCurrency(project.totalAmount)}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Paid Amount:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-paid']}`}>
                                                {formatCurrency(project.totalPaidAmount)}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Remaining:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-remaining']}`}>
                                                {formatCurrency(project.totalRemainingAmount)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <Pagination limit={limit} onLimitChange={handleLimitChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPageChange={(page) => setCurrentPage(page)}
                    currentCount={data.length}
                    entityName="Project"
                />
            </div>
        </div>
    );
};

export default VendorProjectSummary;
